/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useConferenceForm.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing conference form state, validation, and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createConference, updateConference } from '../api/conferencesApi';
import { createDefaultConferenceData } from '../utils/formDefaults';
import type { ConferenceRoom, ValidationErrors } from '../types';

interface UseConferenceFormOptions {
  formData: ConferenceRoom;
  setFormData: React.Dispatch<React.SetStateAction<ConferenceRoom>>;
  originalFormData: ConferenceRoom;
  mode: 'create' | 'edit';
  conferenceId?: string;
  onSave?: (conferenceData: ConferenceRoom) => Promise<ConferenceRoom>;
}

export function useConferenceForm({
  formData,
  setFormData,
  originalFormData,
  mode,
  conferenceId,
  onSave,
}: UseConferenceFormOptions) {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    if (!formData.extension || !formData.extension.trim()) {
      newErrors.extension = t('conferences:validation.extensionRequired');
    } else if (!/^\d+$/.test(formData.extension.trim())) {
      newErrors.extension = t('conferences:validation.extensionNumeric');
    }

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('conferences:validation.nameRequired');
    }

    if (formData.maxMembers < 0) {
      newErrors.maxMembers = t('conferences:validation.maxParticipantsMin');
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData, t]);

  // Handle save
  const handleSave = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('conferences:messages.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    setIsSaving(true);
    try {
      let savedData: ConferenceRoom;

      if (onSave) {
        // Use custom onSave if provided
        savedData = await onSave(formData);
      } else {
        // Use API directly
        if (mode === 'edit' && conferenceId) {
          savedData = await updateConference(conferenceId, formData);
        } else {
          savedData = await createConference(formData);
        }
      }

      if (!savedData) {
        throw new Error('Save operation did not return data');
      }

      showSuccess(
        mode === 'create'
          ? t('conferences:messages.createSuccess')
          : t('conferences:messages.updateSuccess')
      );

      return savedData;
    } catch (error) {
      console.error('Error saving conference:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('conferences:messages.createError')}: ${errorMessage}`);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    validateForm,
    mode,
    conferenceId,
    hasChanges,
    onSave,
    showSuccess,
    showError,
    showInfo,
    t,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (mode === 'create') {
      setFormData(createDefaultConferenceData());
    } else {
      setFormData({ ...originalFormData });
    }
    setErrors({});
  }, [mode, originalFormData, setFormData]);

  return {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    validateForm,
    handleSave,
    handleCancel,
  };
}

